/**
 * My Transversal Skills — auto-évaluation transversale standalone (Lot 2).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ApiRequestError,
  configApi,
  transversalAssessmentApi,
  transversalScoresApi,
  type TransversalScoreDto,
  type TransversalSessionDto,
} from "../api/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/ui/StatusBadge";

type ScaleLevel = { level: number; labelEn: string; labelFr: string };

const DEFAULT_SCALE: ScaleLevel[] = [
  { level: 0, labelEn: "None", labelFr: "Aucun" },
  { level: 1, labelEn: "Basic", labelFr: "Basique" },
  { level: 2, labelEn: "Intermediate", labelFr: "Intermédiaire" },
  { level: 3, labelEn: "Advanced", labelFr: "Avancé" },
  { level: 4, labelEn: "Expert", labelFr: "Expert" },
];

const SCORE_CLS = ["sm-score-0", "sm-score-1", "sm-score-2", "sm-score-3", "sm-score-4"] as const;

function scoreClass(s: number | null): string {
  if (s === null || s < 0 || s > 4) return "";
  return SCORE_CLS[s];
}

function significantTv(value: string | null | undefined): string | null {
  if (value == null) return null;
  const t = value.trim();
  if (!t || t === ".") return null;
  return t;
}

function groupTransversal(lines: TransversalScoreDto[]) {
  type SubMap = Map<string, TransversalScoreDto[]>;
  const mpMap = new Map<string, Map<string, SubMap>>();
  for (const line of lines) {
    const mp = line.macroProcess || "—";
    const bp = line.businessProcess || "—";
    const subKey = significantTv(line.subProcess) ?? "";
    if (!mpMap.has(mp)) mpMap.set(mp, new Map());
    const bpMap = mpMap.get(mp)!;
    if (!bpMap.has(bp)) bpMap.set(bp, new Map());
    const subMap = bpMap.get(bp)!;
    if (!subMap.has(subKey)) subMap.set(subKey, []);
    subMap.get(subKey)!.push(line);
  }
  return Array.from(mpMap.entries()).map(([mp, bpMap]) => ({
    mp,
    bps: Array.from(bpMap.entries()).map(([bp, subMap]) => ({
      bp,
      subs: Array.from(subMap.entries()).map(([sub, groupLines]) => ({
        sub: sub || null,
        lines: groupLines,
      })),
    })),
  }));
}

export function MyTransversalSkillsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const langFr = i18n.language?.toLowerCase().startsWith("fr");
  const isExpert = (user?.accessLevel ?? 0) < 50 || (user?.accessLevel ?? 0) >= 100;

  const [year, setYear] = useState(new Date().getFullYear());
  const [scores, setScores] = useState<TransversalScoreDto[]>([]);
  const [session, setSession] = useState<TransversalSessionDto | null>(null);
  const [scale, setScale] = useState<ScaleLevel[]>(DEFAULT_SCALE);
  const [values, setValues] = useState<Record<string, { score: number | null; comment: string }>>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitBusy, setSubmitBusy] = useState(false);

  const valuesRef = useRef(values);
  valuesRef.current = values;
  const flushYearRef = useRef(year);
  const dirtyRef = useRef(new Set<string>());
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    void configApi.listScale().then((r) => {
      if (r.scale.length > 0) {
        setScale(r.scale.map((s) => ({ level: s.level, labelEn: s.labelEn, labelFr: s.labelFr })));
      }
    });
  }, []);

  const flushSave = useCallback(async () => {
    const dirty = Array.from(dirtyRef.current);
    if (dirty.length === 0) {
      setSaved(true);
      return;
    }
    const current = valuesRef.current;
    const saveYear = flushYearRef.current;
    const lines = dirty.map((id) => {
      const v = current[id] ?? { score: null, comment: "" };
      return {
        transversalSkillId: id,
        score: v.score,
        comment: v.comment.trim() === "" ? null : v.comment,
      };
    });
    setBusy(true);
    try {
      await transversalScoresApi.patch(saveYear, lines);
      dirtyRef.current = new Set();
      setSaved(true);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("save_failed"));
    } finally {
      setBusy(false);
    }
  }, [t]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await transversalScoresApi.get(year);
      setScores(res.scores);
      setSession(res.session);
      const map: Record<string, { score: number | null; comment: string }> = {};
      for (const s of res.scores) {
        map[s.transversalSkillId] = { score: s.score, comment: s.comment ?? "" };
      }
      setValues(map);
      dirtyRef.current = new Set();
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("load_failed"));
    }
  }, [year, t]);

  useEffect(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    void flushSave().then(() => load());
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [load, flushSave]);

  function onChange(skillId: string, score: number | null, comment?: string) {
    flushYearRef.current = year;
    // Un nouvel edit invalide un éventuel message d'erreur résiduel (ex. soumission
    // refusée pour incomplétude avant que ce score ne soit saisi).
    setError(null);
    setValues((prev) => {
      const cur = prev[skillId] ?? { score: null, comment: "" };
      return {
        ...prev,
        [skillId]: {
          score,
          comment: comment !== undefined ? comment : cur.comment,
        },
      };
    });
    dirtyRef.current.add(skillId);
    setSaved(false);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      void flushSave();
    }, 1200);
  }

  async function onSubmit() {
    if (!session) return;
    setSubmitBusy(true);
    setError(null);
    try {
      await flushSave();
      await transversalAssessmentApi.submit(session.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("submit_failed"));
    } finally {
      setSubmitBusy(false);
    }
  }

  const readOnly = !isExpert;
  const total = scores.length;
  const scored = scores.filter((s) => (values[s.transversalSkillId]?.score ?? null) !== null).length;
  const pct = total > 0 ? Math.round((scored / total) * 100) : 0;
  const grouped = groupTransversal(scores);

  return (
    <div className="sm-page">
      <div className="sm-page-header">
        <div>
          <h1>{t("my_transversal_skills_title")}</h1>
          <p className="sm-muted">{t("my_transversal_skills_sub")}</p>
        </div>
        {!saved && (
          <span style={{ fontSize: "0.85em", color: "#f59e0b", fontWeight: 500 }}>
            {busy ? t("saving") : t("unsaved_changes")}
          </span>
        )}
        {saved && total > 0 && (
          <span style={{ fontSize: "0.85em", color: "#22c55e", fontWeight: 500 }}>
            ✓ {t("saved")}
          </span>
        )}
      </div>

      {error ? <div className="sm-alert sm-alert-error">{error}</div> : null}

      <div className="sm-card sm-card-pad" style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        <label className="sm-label">
          {t("year")}
          <select className="sm-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        {total > 0 ? (
          <div className="sm-muted" style={{ alignSelf: "end" }}>
            {t("progress")}: {scored}/{total} ({pct}%)
          </div>
        ) : null}
      </div>

      {session ? (
        <div
          className="sm-card sm-card-pad"
          style={{
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div>
            <h2 style={{ marginTop: 0, marginBottom: 4, fontSize: "1.05rem" }}>
              {t("transversal_submission_status")}
            </h2>
            <p className="sm-muted" style={{ margin: 0 }}>
              {t("transversal_shared_hint")}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <StatusBadge status={session.status} />
            {session.status === "draft" && !readOnly ? (
              <button
                type="button"
                className="sm-btn sm-btn-primary sm-btn-sm"
                disabled={submitBusy || total === 0 || scored < total}
                title={scored < total ? t("submit_needs_all_scored") : undefined}
                onClick={() => void onSubmit()}
              >
                {submitBusy ? t("loading") : t("submit")}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {grouped.map(({ mp, bps }) => (
        <div key={mp} className="sm-card sm-assess-block" style={{ marginBottom: 16 }}>
          <div className="sm-assess-macro sm-assess-macro-tv">{mp}</div>
          {bps.map(({ bp, subs }) => (
            <div key={bp}>
              <div className="sm-assess-bp">{bp}</div>
              {subs.map(({ sub, lines }) => (
                <div key={`${bp}|${sub ?? ""}`}>
                  {sub ? <div className="sm-assess-sub-tv">{sub}</div> : null}
                  {lines.map((line) => {
                    const v = values[line.transversalSkillId] ?? { score: null, comment: "" };
                    const task = significantTv(line.task);
                    return (
                      <div key={line.transversalSkillId} className="sm-assess-line">
                        <div className="sm-assess-line-label">
                          {task ?? sub ?? bp}
                        </div>
                        <div className="sm-assess-scores">
                          {scale.map((s) => (
                            <button
                              key={s.level}
                              type="button"
                              disabled={readOnly}
                              title={langFr ? s.labelFr : s.labelEn}
                              className={`sm-score-btn${
                                v.score === s.level ? ` ${scoreClass(s.level)}` : ""
                              }`}
                              onClick={() =>
                                onChange(
                                  line.transversalSkillId,
                                  v.score === s.level ? null : s.level
                                )
                              }
                            >
                              {s.level}
                            </button>
                          ))}
                        </div>
                        {!readOnly ? (
                          <input
                            className="sm-input sm-assess-comment"
                            placeholder={t("add_comment")}
                            value={v.comment}
                            onChange={(e) =>
                              onChange(line.transversalSkillId, v.score, e.target.value)
                            }
                          />
                        ) : v.comment ? (
                          <span className="sm-assess-comment-ro">{v.comment}</span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
